// frontend/src/components/planner/MatchingPanel.jsx
// Phase 2.6B: Transaction Matching UI — Live SSE Stepper + Staggered Match Reveal

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import {
  RefreshCw,
  Check,
  X,
  Link2,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Building2,
  Banknote,
  Zap,
  Brain,
  User,
  HelpCircle,
  ArrowRight,
  Calendar,
  DollarSign,
  Terminal,
  Wifi,
  Search,
  Cpu,
  CheckCircle2,
  Loader2,
  Sparkles
} from 'lucide-react';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import AppShell from '../layout/AppShell';
import { useBusinessEntity } from '../../contexts/BusinessEntityContext';
import { useSync } from '../../contexts/SyncContext';

// ─── Constants ───────────────────────────────────────────────────────────────

const CONFIDENCE_CONFIG = {
  high:   { min: 0.85, color: 'bg-emerald-500', textColor: 'text-emerald-600', label: 'High' },
  medium: { min: 0.60, color: 'bg-amber-500',   textColor: 'text-amber-600',   label: 'Medium' },
  low:    { min: 0,    color: 'bg-red-500',      textColor: 'text-red-600',     label: 'Low' }
};

const METHOD_CONFIG = {
  exact:  { icon: Zap,   label: 'Exact Match',  color: 'text-emerald-600' },
  fuzzy:  { icon: Link2, label: 'Fuzzy Match',  color: 'text-blue-600' },
  ai:     { icon: Brain, label: 'AI Match',     color: 'text-purple-600' },
  manual: { icon: User,  label: 'Manual',       color: 'text-gray-600' }
};

const STEPS = [
  { step: 1, label: 'Connect',  icon: Wifi },
  { step: 2, label: 'Fetch',    icon: Search },
  { step: 3, label: 'AI Score', icon: Cpu },
  { step: 4, label: 'Complete', icon: CheckCircle2 }
];

const STAGGER_DELAY_MS = 350;

// ─── Utilities ───────────────────────────────────────────────────────────────

function formatCurrency(amount) {
  const num = parseFloat(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2
  }).format(Math.abs(num));
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return format(parseISO(dateStr + 'T00:00:00'), 'MMM d, yyyy');
}

function formatShortDate(dateStr) {
  if (!dateStr) return '';
  return format(parseISO(dateStr + 'T00:00:00'), 'MMM d');
}

function getConfidenceLevel(confidence) {
  const pct = parseFloat(confidence) || 0;
  if (pct >= CONFIDENCE_CONFIG.high.min) return 'high';
  if (pct >= CONFIDENCE_CONFIG.medium.min) return 'medium';
  return 'low';
}

// ─── Sub-Components ──────────────────────────────────────────────────────────

/** Stepper indicator showing sync progress */
function SyncStepper({ currentStep, stepLabel, isDone }) {
  return (
    <div className="flex items-center gap-1 w-full">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const isComplete = currentStep > s.step || (isDone && currentStep >= s.step);
        const isActive = !isDone && currentStep === s.step;
        const isPending = currentStep < s.step;

        return (
          <React.Fragment key={s.step}>
            {/* Step circle */}
            <div className="flex flex-col items-center min-w-0">
              <div className={`
                w-9 h-9 rounded-full flex items-center justify-center transition-all duration-500
                ${isComplete ? 'bg-emerald-500 text-white' : ''}
                ${isActive ? 'bg-blue-500 text-white ring-4 ring-blue-100' : ''}
                ${isPending ? 'bg-gray-200 text-gray-400' : ''}
              `}>
                {isComplete ? (
                  <Check className="w-4 h-4" />
                ) : isActive ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <span className={`text-xs mt-1 text-center whitespace-nowrap ${
                isActive ? 'text-blue-600 font-medium' : isComplete ? 'text-emerald-600' : 'text-gray-400'
              }`}>
                {isActive ? (stepLabel || s.label) : s.label}
              </span>
            </div>
            {/* Connector line */}
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 transition-all duration-500 ${
                currentStep > s.step ? 'bg-emerald-400' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/** Skeleton placeholder card for unrevealed matches */
function SkeletonMatchCard() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-100 animate-pulse">
      <div className="w-6 h-6 bg-gray-200 rounded" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-100 rounded w-1/2" />
      </div>
      <div className="w-24 space-y-1">
        <div className="h-2 bg-gray-200 rounded" />
        <div className="h-3 bg-gray-100 rounded w-12 mx-auto" />
      </div>
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-2/3" />
        <div className="h-3 bg-gray-100 rounded w-1/2" />
      </div>
      <div className="flex gap-2">
        <div className="h-8 w-20 bg-gray-200 rounded-lg" />
        <div className="h-8 w-8 bg-gray-200 rounded-lg" />
      </div>
    </div>
  );
}

/** Activity log panel */
function ActivityLog({ logs, isOpen, onToggle }) {
  const logEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isOpen]);

  return (
    <div className="mt-3">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] sm:min-h-0"
      >
        <Terminal className="w-3 h-3" />
        <span>{isOpen ? 'Hide' : 'Show'} activity log ({logs.length})</span>
        {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {isOpen && (
        <div className="mt-2 bg-gray-900 rounded-lg p-3 max-h-48 overflow-y-auto font-mono text-xs text-gray-300">
          {logs.length === 0 && <div className="text-gray-500">Waiting...</div>}
          {logs.map((line, i) => (
            <div key={i} className="py-0.5">
              <span className="text-gray-500 mr-2">{line.time}</span>
              <span className={
                line.message.startsWith('✓') ? 'text-emerald-400' :
                line.message.startsWith('✗') ? 'text-red-400' :
                line.message.startsWith('↳') ? 'text-purple-400' :
                'text-gray-300'
              }>
                {line.message}
              </span>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function MatchingPanel() {
  const { activeFilter, entities } = useBusinessEntity();
  const navigate = useNavigate();
  const { triggerSync, syncing: globalSyncing, onStageRef, onMatchRef, onAutoMatchRef, onLogRef, onRecurringRef, onCompleteRef, onErrorRef, lastSyncAt, refreshBadgeCounts } = useSync();

  // Data state
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState(null);

  // SSE sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStep, setSyncStep] = useState(0);       // 0=not started, 1-4=steps
  const [syncStepLabel, setSyncStepLabel] = useState('');
  const [skeletonCount, setSkeletonCount] = useState(0);
  const [revealedMatches, setRevealedMatches] = useState([]);
  const [syncComplete, setSyncComplete] = useState(null);
  const [logLines, setLogLines] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const revealQueueRef = useRef([]);
  const revealTimerRef = useRef(null);

  // Auto-matched items from SSE (high confidence + match history)
  const [autoMatched, setAutoMatched] = useState([]);

  // Recurring detection result from SSE
  const [recurringResult, setRecurringResult] = useState(null); // { newPatterns, totalPending, patterns }
  const [pendingSuggestionCount, setPendingSuggestionCount] = useState(0); // Persistent count

  // Filter state
  const [showHighConfidence, setShowHighConfidence] = useState(true);
  const [showMediumConfidence, setShowMediumConfidence] = useState(true);
  const [showLowConfidence, setShowLowConfidence] = useState(true);

  // Processing state (accept/reject)
  const [processingId, setProcessingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Amount update prompt state (Phase 3)
  const [amountPrompt, setAmountPrompt] = useState(null); // { suggestionId, plaidAmount, scheduledAmount }

  // ── Clean up SSE callback refs on unmount ────────────────────────────
  useEffect(() => {
    return () => {
      // Prevent dead ref calls if user navigates away mid-sync
      if (onStageRef) onStageRef.current = null;
      if (onMatchRef) onMatchRef.current = null;
      if (onAutoMatchRef) onAutoMatchRef.current = null;
      if (onLogRef) onLogRef.current = null;
      if (onRecurringRef) onRecurringRef.current = null;
      if (onCompleteRef) onCompleteRef.current = null;
      if (onErrorRef) onErrorRef.current = null;
    };
  }, [onStageRef, onMatchRef, onAutoMatchRef, onLogRef, onRecurringRef, onCompleteRef, onErrorRef]);

  // ── Load on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    loadBackfillStatus();
    loadSuggestions();
    loadPendingSuggestionCount();
    return () => {
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    };
  }, []);

  // ── Reload when global sync completes from another page ──────────────
  useEffect(() => {
    if (lastSyncAt && !isSyncing) {
      loadSuggestions();
      loadBackfillStatus();
    }
  }, [lastSyncAt]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadPendingSuggestionCount() {
    try {
      const res = await api.get('/api/planner/recurring-suggestions/count');
      if (res.data.success) setPendingSuggestionCount(res.data.count);
    } catch (err) {
      // Silently ignore
    }
  }

  async function loadBackfillStatus() {
    try {
      const response = await api.get('/api/planner/backfill/status');
      if (response.data.success) setBackfillStatus(response.data);
    } catch (err) {
      console.error('Error loading backfill status:', err);
    }
  }

  async function loadSuggestions() {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/planner/suggested-matches?status=pending');
      if (response.data.success) {
        setSuggestions(response.data.suggestions || []);
      }
    } catch (err) {
      console.error('Error loading suggestions:', err);
      setError('Failed to load match suggestions');
    } finally {
      setLoading(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await loadSuggestions();
    setRefreshing(false);
  }

  // ── SSE Sync & Match ──────────────────────────────────────────────────

  function addLog(message) {
    const time = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLogLines(prev => [...prev, { time, message }]);
  }

  /** Process the staggered reveal queue */
  function processRevealQueue() {
    if (revealQueueRef.current.length === 0) return;
    const next = revealQueueRef.current.shift();
    setRevealedMatches(prev => [...prev, next]);
    setSkeletonCount(prev => Math.max(0, prev - 1));

    if (revealQueueRef.current.length > 0) {
      revealTimerRef.current = setTimeout(processRevealQueue, STAGGER_DELAY_MS);
    }
  }

  async function handleSyncAndMatch() {
    if (isSyncing || globalSyncing) return;

    // Reset local UI state
    setIsSyncing(true);
    setSyncStep(0);
    setSyncStepLabel('');
    setSkeletonCount(0);
    setRevealedMatches([]);
    setAutoMatched([]);
    setSyncComplete(null);
    setRecurringResult(null);
    setLogLines([]);
    setError('');
    revealQueueRef.current = [];
    if (revealTimerRef.current) clearTimeout(revealTimerRef.current);

    addLog('Starting Sync & Match...');

    // Subscribe to context events via ref-based callbacks
    onStageRef.current = (data) => {
      if (data.type === 'skeletons') {
        setSkeletonCount(data.count || 0);
      } else {
        setSyncStep(data.step || 0);
        setSyncStepLabel(data.label || '');
      }
    };
    onMatchRef.current = (data) => {
      revealQueueRef.current.push(data.suggestion || data);
      if (revealQueueRef.current.length === 1) {
        revealTimerRef.current = setTimeout(processRevealQueue, STAGGER_DELAY_MS);
      }
    };
    onAutoMatchRef.current = (data) => {
      setAutoMatched(prev => [...prev, data.item || data]);
    };
    onLogRef.current = (data) => {
      addLog(data.message || data);
    };
    onRecurringRef.current = (data) => {
      setRecurringResult(data);
      setPendingSuggestionCount(data.totalPending || 0);
    };
    onCompleteRef.current = (data) => {
      setSyncComplete(data.summary || data);
      setTimeout(async () => {
        await loadBackfillStatus();
        await loadSuggestions();
      }, 1500);
    };
    onErrorRef.current = (data) => {
      setError(data.message || 'Sync failed');
      addLog(`✗ ${data.message || 'Sync failed'}`);
    };

    // Trigger the global sync (SSE managed by SyncContext)
    try {
      await triggerSync();
    } finally {
      // Cleanup callbacks even if triggerSync throws
      onStageRef.current = null;
      onMatchRef.current = null;
      onAutoMatchRef.current = null;
      onLogRef.current = null;
      onRecurringRef.current = null;
      onCompleteRef.current = null;
      onErrorRef.current = null;
      setIsSyncing(false);
    }
  }

  // ── Actions ────────────────────────────────────────────────────────────

  async function handleAccept(suggestionId, amountAction) {
    // If no explicit amountAction provided, check if amounts differ enough to prompt
    if (!amountAction) {
      const suggestion = [...suggestions, ...revealedMatches].find(s => s.id === suggestionId);
      if (suggestion) {
        const plaidAmt = Math.abs(parseFloat(suggestion.plaidTransaction?.amount) || 0);
        const schedAmt = Math.abs(parseFloat(suggestion.suggestedTransaction?.amount) || 0);
        const maxAmt = Math.max(plaidAmt, schedAmt);
        const pctDiff = maxAmt > 0 ? Math.abs(plaidAmt - schedAmt) / maxAmt : 0;
        if (pctDiff > 0.05) {
          // Show the amount update prompt instead of accepting immediately
          setAmountPrompt({ suggestionId, plaidAmount: plaidAmt, scheduledAmount: schedAmt });
          return;
        }
      }
      amountAction = 'keep'; // amounts match, no update needed
    }

    try {
      setProcessingId(suggestionId);
      setAmountPrompt(null);
      const response = await api.post(`/api/planner/suggested-matches/${suggestionId}/accept`, { amountAction });
      if (response.data.success) {
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        setRevealedMatches(prev => prev.filter(s => s.id !== suggestionId));
        refreshBadgeCounts();
      }
    } catch (err) {
      console.error('Error accepting match:', err);
      setError('Failed to accept match');
    } finally {
      setProcessingId(null);
    }
  }

  async function handleReject(suggestionId) {
    try {
      setProcessingId(suggestionId);
      const response = await api.post(`/api/planner/suggested-matches/${suggestionId}/reject`, { createNew: true });
      if (response.data.success) {
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        setRevealedMatches(prev => prev.filter(s => s.id !== suggestionId));
        refreshBadgeCounts();
      }
    } catch (err) {
      console.error('Error rejecting match:', err);
      setError('Failed to reject match');
    } finally {
      setProcessingId(null);
    }
  }

  async function handleUndoAutoMatch(item) {
    try {
      setProcessingId(item.scheduledTransactionId);
      const response = await api.post('/api/planner/unmatch', {
        scheduledTransactionId: item.scheduledTransactionId,
        plaidTransactionId: item.plaidTransactionId
      });
      if (response.data.success) {
        setAutoMatched(prev => prev.filter(a => a.scheduledTransactionId !== item.scheduledTransactionId));
      }
    } catch (err) {
      console.error('Error undoing auto-match:', err);
      setError('Failed to undo auto-match');
    } finally {
      setProcessingId(null);
    }
  }

  // ── Filtering ──────────────────────────────────────────────────────────

  const displaySource = isSyncing || (syncComplete && revealedMatches.length > 0 && suggestions.length === 0)
    ? revealedMatches
    : suggestions;

  // Build bank_account_id → business_entity_id map from context entities
  const businessAccountMap = useMemo(() => {
    const map = {};
    (entities || []).forEach(e => {
      (e.linkedAccountIds || []).forEach(id => { map[id] = e.id; });
    });
    return map;
  }, [entities]);

  // Filter by entity first, then by confidence
  const entityFiltered = useMemo(() => {
    if (activeFilter === 'all') return displaySource;
    return displaySource.filter(s => {
      const bankAccountId = s.suggestedTransaction?.bank_account_id;
      const entityId = businessAccountMap[bankAccountId];
      if (activeFilter === 'personal') return !entityId;
      return entityId === activeFilter;
    });
  }, [displaySource, activeFilter, businessAccountMap]);

  const filteredSuggestions = entityFiltered.filter(s => {
    const level = getConfidenceLevel(s.confidence);
    if (level === 'high' && !showHighConfidence) return false;
    if (level === 'medium' && !showMediumConfidence) return false;
    if (level === 'low' && !showLowConfidence) return false;
    return true;
  });

  const countSummary = {
    high:   entityFiltered.filter(s => getConfidenceLevel(s.confidence) === 'high').length,
    medium: entityFiltered.filter(s => getConfidenceLevel(s.confidence) === 'medium').length,
    low:    entityFiltered.filter(s => getConfidenceLevel(s.confidence) === 'low').length
  };

  function toggleExpanded(id) {
    setExpandedId(prev => prev === id ? null : id);
  }

  // ── Render helpers ─────────────────────────────────────────────────────

  function renderConfidenceBar(confidence) {
    const pct = Math.round((parseFloat(confidence) || 0) * 100);
    const level = getConfidenceLevel(confidence);
    const config = CONFIDENCE_CONFIG[level];

    return (
      <div className="flex items-center gap-2">
        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full ${config.color} transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <span className={`text-xs font-medium ${config.textColor}`}>{pct}%</span>
      </div>
    );
  }

  function renderSuggestionRow(suggestion, isRevealing = false) {
    const isProcessing = processingId === suggestion.id;
    const isExpanded = expandedId === suggestion.id;
    const methodConfig = METHOD_CONFIG[suggestion.match_method] || METHOD_CONFIG.manual;
    const MethodIcon = methodConfig.icon;

    const plaidTx = suggestion.plaidTransaction;
    const scheduledTx = suggestion.suggestedTransaction;
    if (!plaidTx || !scheduledTx) return null;

    const plaidAmount = Math.abs(parseFloat(plaidTx.amount) || 0);
    const scheduledAmount = Math.abs(parseFloat(scheduledTx.amount) || 0);
    const amountMatch = Math.abs(plaidAmount - scheduledAmount) < 0.01;

    return (
      <div
        key={suggestion.id}
        className={`border-b border-gray-100 last:border-b-0 transition-all duration-500 ${
          isRevealing ? 'animate-fadeSlideIn' : ''
        }`}
      >
        {/* === MOBILE CARD (below sm) === */}
        <div className={`sm:hidden p-3 bg-white active:bg-gray-50 ${
          isProcessing ? 'opacity-50 pointer-events-none' : ''
        }`}>
          {/* Bank Transaction */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Building2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <div className="min-w-0">
                <span className="text-base font-medium text-gray-900 truncate block">
                  {plaidTx.merchant_name || plaidTx.name}
                </span>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                  <span>{formatShortDate(plaidTx.date)}</span>
                  <span>{formatCurrency(plaidTx.amount)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Confidence + Method */}
          <div className="flex items-center gap-2 mb-2 ml-6">
            <MethodIcon className={`w-3.5 h-3.5 ${methodConfig.color}`} title={methodConfig.label} />
            <ArrowRight className="w-3.5 h-3.5 text-gray-300" />
            {renderConfidenceBar(suggestion.confidence)}
          </div>

          {/* Forecast Item */}
          <div className="flex items-center gap-2 mb-3 ml-6">
            <Banknote className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <div className="min-w-0">
              <span className="text-base font-medium text-gray-900 truncate block">{scheduledTx.name}</span>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                <span>{formatShortDate(scheduledTx.scheduled_date)}</span>
                <span className={amountMatch ? '' : 'text-amber-600'}>
                  {formatCurrency(scheduledTx.amount)}
                  {!amountMatch && ' (differs)'}
                </span>
              </div>
            </div>
          </div>

          {/* Actions + expand */}
          <div className="flex items-center justify-between">
            <button onClick={() => toggleExpanded(suggestion.id)} className="p-2 hover:bg-gray-100 rounded min-w-[44px] min-h-[44px] flex items-center justify-center">
              {isExpanded
                ? <ChevronDown className="w-4 h-4 text-gray-400" />
                : <ChevronRight className="w-4 h-4 text-gray-400" />}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleAccept(suggestion.id)}
                disabled={isProcessing}
                className="flex items-center gap-1 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors text-base sm:text-sm font-medium min-h-[44px]"
                title="Confirm match"
              >
                <Check className="w-4 h-4" />Match
              </button>
              <button
                onClick={() => handleReject(suggestion.id)}
                disabled={isProcessing}
                className="flex items-center gap-1 px-4 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-base sm:text-sm min-h-[44px]"
                title="Reject"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* === DESKTOP ROW (sm+) === */}
        <div className={`hidden sm:flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors min-w-0 ${
          isProcessing ? 'opacity-50 pointer-events-none' : ''
        }`}>
          <button onClick={() => toggleExpanded(suggestion.id)} className="p-1 hover:bg-gray-100 rounded flex-shrink-0">
            {isExpanded
              ? <ChevronDown className="w-4 h-4 text-gray-400" />
              : <ChevronRight className="w-4 h-4 text-gray-400" />}
          </button>

          {/* Bank Transaction (Left Side) */}
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="font-medium text-gray-900 truncate">
                {plaidTx.merchant_name || plaidTx.name}
              </span>
            </div>
            {plaidTx.account_display && (
              <div className="text-xs text-blue-600 mt-0.5 truncate">{plaidTx.account_display}</div>
            )}
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
              <span className="flex items-center gap-1 flex-shrink-0">
                <Calendar className="w-3 h-3" />{formatShortDate(plaidTx.date)}
              </span>
              <span className="flex items-center gap-1 flex-shrink-0">
                <DollarSign className="w-3 h-3" />{formatCurrency(plaidTx.amount)}
              </span>
            </div>
          </div>

          {/* Arrow + Confidence */}
          <div className="flex flex-col items-center gap-1 px-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <MethodIcon className={`w-4 h-4 ${methodConfig.color}`} title={methodConfig.label} />
              <ArrowRight className="w-4 h-4 text-gray-300" />
            </div>
            {renderConfidenceBar(suggestion.confidence)}
          </div>

          {/* Forecast Item (Right Side) */}
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center gap-2">
              <Banknote className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className="font-medium text-gray-900 truncate">{scheduledTx.name}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
              <span className="flex items-center gap-1 flex-shrink-0">
                <Calendar className="w-3 h-3" />{formatShortDate(scheduledTx.scheduled_date)}
              </span>
              <span className={`flex items-center gap-1 flex-shrink-0 ${amountMatch ? '' : 'text-amber-600'}`}>
                <DollarSign className="w-3 h-3" />{formatCurrency(scheduledTx.amount)}
                {!amountMatch && <HelpCircle className="w-3 h-3" title="Amount differs" />}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => handleAccept(suggestion.id)}
              disabled={isProcessing}
              className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors text-sm font-medium"
              title="Confirm match"
            >
              <Check className="w-4 h-4" />Match
            </button>
            <button
              onClick={() => handleReject(suggestion.id)}
              disabled={isProcessing}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-sm"
              title="Reject"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Amount Update Prompt (Phase 3) */}
        {amountPrompt?.suggestionId === suggestion.id && (
          <div className="px-4 py-3 bg-amber-50 border-t border-amber-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-base sm:text-sm font-medium text-gray-800">
                  Amounts differ — how would you like to update your forecast?
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Bank: {formatCurrency(amountPrompt.plaidAmount)} vs Forecast: {formatCurrency(amountPrompt.scheduledAmount)}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    onClick={() => handleAccept(suggestion.id, 'keep')}
                    className="px-3 py-1.5 text-base sm:text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors min-h-[44px] sm:min-h-0"
                  >
                    Keep forecast amount
                  </button>
                  <button
                    onClick={() => handleAccept(suggestion.id, 'update_instance')}
                    className="px-3 py-1.5 text-base sm:text-sm rounded-lg border border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors min-h-[44px] sm:min-h-0"
                  >
                    Update this instance only
                  </button>
                  {scheduledTx.recurring_item_id && (
                    <button
                      onClick={() => handleAccept(suggestion.id, 'update_all')}
                      className="px-3 py-1.5 text-base sm:text-sm rounded-lg border border-emerald-300 bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-colors font-medium min-h-[44px] sm:min-h-0"
                    >
                      Update all future occurrences
                    </button>
                  )}
                  <button
                    onClick={() => setAmountPrompt(null)}
                    className="px-3 py-1.5 text-base sm:text-sm rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px] sm:min-h-0"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expanded details */}
        {isExpanded && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 text-base sm:text-sm">
              {/* Bank Transaction Details */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-500" />Bank Transaction
                </h4>
                <dl className="space-y-1 text-gray-600">
                  <div className="flex"><dt className="w-24 text-gray-500">Name:</dt><dd>{plaidTx.name}</dd></div>
                  {plaidTx.merchant_name && plaidTx.merchant_name !== plaidTx.name && (
                    <div className="flex"><dt className="w-24 text-gray-500">Merchant:</dt><dd>{plaidTx.merchant_name}</dd></div>
                  )}
                  <div className="flex"><dt className="w-24 text-gray-500">Date:</dt><dd>{formatDate(plaidTx.date)}</dd></div>
                  <div className="flex">
                    <dt className="w-24 text-gray-500">Amount:</dt>
                    <dd className={plaidTx.amount < 0 ? 'text-emerald-600' : 'text-red-600'}>
                      {plaidTx.amount < 0 ? '+' : '-'}{formatCurrency(plaidTx.amount)}
                    </dd>
                  </div>
                  {plaidTx.category && (
                    <div className="flex">
                      <dt className="w-24 text-gray-500">Category:</dt>
                      <dd>{Array.isArray(plaidTx.category) ? plaidTx.category.join(' > ') : plaidTx.category}</dd>
                    </div>
                  )}
                  {plaidTx.account_display && (
                    <div className="flex">
                      <dt className="w-24 text-gray-500">Account:</dt>
                      <dd className="text-blue-600">{plaidTx.account_display}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Forecast Item Details */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-emerald-500" />Forecast Item
                </h4>
                <dl className="space-y-1 text-gray-600">
                  <div className="flex"><dt className="w-24 text-gray-500">Name:</dt><dd>{scheduledTx.name}</dd></div>
                  <div className="flex"><dt className="w-24 text-gray-500">Due Date:</dt><dd>{formatDate(scheduledTx.scheduled_date)}</dd></div>
                  <div className="flex">
                    <dt className="w-24 text-gray-500">Amount:</dt>
                    <dd className={scheduledTx.item_type === 'income' ? 'text-emerald-600' : 'text-red-600'}>
                      {scheduledTx.item_type === 'income' ? '+' : '-'}{formatCurrency(scheduledTx.amount)}
                    </dd>
                  </div>
                  <div className="flex"><dt className="w-24 text-gray-500">Type:</dt><dd className="capitalize">{scheduledTx.item_type}</dd></div>
                  {scheduledTx.category && (
                    <div className="flex"><dt className="w-24 text-gray-500">Category:</dt><dd>{scheduledTx.category}</dd></div>
                  )}
                  <div className="flex"><dt className="w-24 text-gray-500">Account:</dt><dd>{scheduledTx.business_entity_id ? (entities.find(e => e.id === scheduledTx.business_entity_id)?.name || 'Business') : 'Personal'}</dd></div>
                </dl>
              </div>
            </div>

            {suggestion.match_reason && (
              <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                <p className="text-base sm:text-sm text-gray-600">
                  <span className="font-medium text-gray-700">Match reason: </span>
                  {suggestion.match_reason}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Main Render ────────────────────────────────────────────────────────

  const remainingSkeletons = Math.max(0, skeletonCount);
  const showSyncView = isSyncing || syncStep > 0;

  return (
    <AppShell>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Transaction Matching</h1>
              <p className="text-base sm:text-sm text-gray-500">Match bank transactions to your forecast items</p>
            </div>
            {/* Sync buttons removed — use global "Sync All" in sidebar */}
          </div>
        </div>

        {/* Initial Sync banner removed — global Sync All handles this */}

        {/* ── Sync Progress Panel ── */}
        {showSyncView && (
          <div className="mx-3 sm:mx-6 mt-4 p-3 sm:p-5 bg-white rounded-lg shadow border border-gray-200">
            <SyncStepper currentStep={syncStep} stepLabel={syncStepLabel} isDone={!!syncComplete} />

            {/* Sync complete summary */}
            {syncComplete && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2 text-base sm:text-sm text-emerald-800">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-medium">
                    Sync complete — {syncComplete.accountsSynced} account(s), {syncComplete.matchesFound} match(es) found
                    {autoMatched.length > 0 && `, ${autoMatched.length} auto-matched`}
                  </span>
                </div>
                {autoMatched.length > 0 && (
                  <div className="mt-2 ml-6">
                    <p className="text-xs font-medium text-emerald-700 mb-1">
                      Auto-matched ({autoMatched.length} items with 85%+ confidence and prior match history):
                    </p>
                    {autoMatched.map((item, i) => (
                      <div key={i} className="text-xs text-emerald-600 flex items-center gap-2 py-0.5">
                        <Check className="w-3 h-3 flex-shrink-0" />
                        <span className="font-medium">{item.name}</span>
                        <span className="text-emerald-400">&bull;</span>
                        <span>{formatCurrency(item.amount)}</span>
                        <span className="text-emerald-400">&bull;</span>
                        <span>{Math.round(item.confidence * 100)}%</span>
                        <span className="text-emerald-400">&bull;</span>
                        <span className="text-emerald-500">{item.matchHistory} prior</span>
                        <button
                          onClick={() => handleUndoAutoMatch(item)}
                          disabled={processingId === item.scheduledTransactionId}
                          className="ml-1 px-1.5 py-0.5 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors min-h-[44px] sm:min-h-0"
                          title="Undo this auto-match"
                        >
                          Undo
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recurring patterns detected banner */}
            {recurringResult && recurringResult.totalPending > 0 && (
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-base sm:text-sm text-purple-800">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    <span className="font-medium">
                      {recurringResult.newPatterns > 0
                        ? `${recurringResult.newPatterns} new recurring pattern(s) detected`
                        : `${recurringResult.totalPending} recurring suggestion(s) awaiting review`
                      }
                    </span>
                  </div>
                  <button
                    onClick={() => navigate('/planner/subscriptions')}
                    className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
                  >
                    Review
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
                {recurringResult.newPatterns > 0 && recurringResult.patterns?.length > 0 && (
                  <div className="mt-2 ml-6 space-y-0.5">
                    {recurringResult.patterns.map((p, i) => (
                      <div key={i} className="text-xs text-purple-600 flex items-center gap-2">
                        <Sparkles className="w-3 h-3" />
                        <span>{p.name}</span>
                        <span className="text-purple-400">&bull;</span>
                        <span>{formatCurrency(p.amount)}</span>
                        <span className="text-purple-400">&bull;</span>
                        <span className="capitalize">{p.frequency}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Activity Log */}
            <ActivityLog logs={logLines} isOpen={logOpen} onToggle={() => setLogOpen(prev => !prev)} />
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="mx-3 sm:mx-6 mt-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-base sm:text-sm text-red-700">{error}</p>
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600 min-h-[44px] min-w-[44px] flex items-center justify-center sm:min-h-0 sm:min-w-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Filter Bar */}
        <div className="px-3 sm:px-6 py-3 sm:py-4">
          <div className="bg-white rounded-lg shadow border border-gray-200 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                <span className="text-base sm:text-sm text-gray-600">Filter:</span>
                {[
                  { key: 'high', value: showHighConfidence, setter: setShowHighConfidence, cls: 'text-emerald-600' },
                  { key: 'medium', value: showMediumConfidence, setter: setShowMediumConfidence, cls: 'text-amber-600' },
                  { key: 'low', value: showLowConfidence, setter: setShowLowConfidence, cls: 'text-red-600' }
                ].map(({ key, value, setter, cls }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer min-h-[44px] sm:min-h-0">
                    <input type="checkbox" checked={value} onChange={e => setter(e.target.checked)}
                      className="rounded border-gray-300 w-5 h-5 sm:w-4 sm:h-4" />
                    <span className={`text-base sm:text-sm ${cls} font-medium`}>
                      {CONFIDENCE_CONFIG[key].label} ({countSummary[key]})
                    </span>
                  </label>
                ))}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">
                {filteredSuggestions.length} of {displaySource.length} shown
              </div>
            </div>
          </div>
        </div>

        {/* ── Match List ── */}
        <div className="px-3 sm:px-6 pb-4 sm:pb-6">
          {/* During sync at AI stage: show revealed matches + skeletons */}
          {isSyncing && syncStep >= 3 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider min-w-0">
                <div className="w-6 flex-shrink-0" />
                <div className="flex-1 min-w-0">Bank Transaction</div>
                <div className="w-28 text-center flex-shrink-0">Confidence</div>
                <div className="flex-1 min-w-0">Forecast Item</div>
                <div className="w-28 text-center flex-shrink-0">Actions</div>
              </div>

              {revealedMatches.map(s => renderSuggestionRow(s, true))}
              {Array.from({ length: remainingSkeletons }).map((_, i) => (
                <SkeletonMatchCard key={`skeleton-${i}`} />
              ))}

              {revealedMatches.length === 0 && remainingSkeletons === 0 && syncStep < 4 && (
                <div className="px-4 py-6 text-center text-base sm:text-sm text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Scanning for matches...
                </div>
              )}
            </div>
          ) : loading && !isSyncing ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
              <p className="text-gray-500">Loading match suggestions...</p>
            </div>
          ) : error && !isSyncing ? (
            <div className="bg-white rounded-lg shadow border border-red-200 p-8 text-center">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-600">{error}</p>
            </div>
          ) : filteredSuggestions.length === 0 && !isSyncing ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
              <Check className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">All Caught Up!</h3>
              <p className="text-gray-500 mb-4">
                {displaySource.length === 0
                  ? "No pending match suggestions. Use 'Sync All' in the sidebar to find new matches."
                  : 'No matches in the selected confidence levels.'}
              </p>
              {/* Persistent subscription review banner */}
              {pendingSuggestionCount > 0 && (
                <div className="mt-4 w-full max-w-md mx-auto p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-base sm:text-sm text-purple-800">
                      <Sparkles className="w-4 h-4 text-purple-600" />
                      <span className="font-medium">
                        {pendingSuggestionCount} subscription(s) to review
                      </span>
                    </div>
                    <button
                      onClick={() => navigate('/planner/subscriptions')}
                      className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
                    >
                      Review
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : !isSyncing ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider min-w-0">
                <div className="w-6 flex-shrink-0" />
                <div className="flex-1 min-w-0">Bank Transaction</div>
                <div className="w-28 text-center flex-shrink-0">Confidence</div>
                <div className="flex-1 min-w-0">Forecast Item</div>
                <div className="w-28 text-center flex-shrink-0">Actions</div>
              </div>
              {filteredSuggestions.map(s => renderSuggestionRow(s))}
            </div>
          ) : null}
        </div>
      </div>

      {/* CSS animation for staggered card reveal */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeSlideIn {
          animation: fadeSlideIn 0.4s ease-out forwards;
        }
      `}</style>
    </AppShell>
  );
}
